import * as signalR from '@microsoft/signalr';
import { NETAPI_URL } from '../config/api';

/**
 * Backend `OnKioskProductVisibilityChanged` event payload tipi.
 *
 * Plan 1.5.1 onayi: gts_dotnetapi `AddSignalR()` cagrisi `AddJsonProtocol(...)` ile
 * ozellestirilmemis, default `JsonHubProtocol` PropertyNamingPolicy = null →
 * **PascalCase wire**. Bu yuzden interface PascalCase tanimli.
 *
 * Defansif olarak (gelecekte JsonProtocol policy degisirse kirilmasin) servisin
 * `OnKioskProductVisibilityChanged` listener'inda hem PascalCase hem camelCase
 * key'lerden okuma yapilir (asagida).
 */
export interface KioskProductVisibilityChangedPayload {
  KioskId: number;
  FirebaseProductId: string;
  IsHidden: boolean;
}

class KioskSignalRService {
  private connection: signalR.HubConnection | null = null;
  private companyId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /** id -> callback. Map sayesinde aynı event'e birden çok bileşen abone olabilir
   *  ve unmount sırasında temiz unsubscribe olur (HMR-safe). */
  private changedCallbacks: Map<string, (payload: KioskProductVisibilityChangedPayload) => void> = new Map();
  private statusChangeCallbacks: ((state: signalR.HubConnectionState) => void)[] = [];

  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }

  /**
   * Web admin oturumlari icin sirket grubuna katilim. Plan 1 backend `set` cagrildiginda
   * sirketteki tum kioskler icin event'i `Company_{companyId}` grubuna yayinlar; modal
   * kiosk basina sekme ile filtre yapar (`payload.KioskId` ile).
   *
   * NOT: Web client `JoinKioskGroup` cagirmaz — yoksa ayni event'i 2 kez alir
   * (backend hem Company_ hem Kiosk_ gruplarina yayin yapiyor; kiosk app'leri sadece
   * Kiosk_ grubuna katiliyor — Plan 3).
   */
  async connect(companyId: string): Promise<void> {
    console.log('🔌 [KioskSignalR] connect() ÇAĞRILDI:', {
      companyId,
      currentState: this.connection?.state,
      currentCompanyId: this.companyId,
      hubUrl: `${NETAPI_URL}/hubs/kiosk`,
    });

    if (!companyId) {
      console.warn('[KioskSignalR] companyId boş, bağlantı atlandı.');
      return;
    }

    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('[KioskSignalR] 🟢 Zaten bağlı, listener re-register + grup kontrolü yapılıyor.');
      this.reRegisterListenersIfNeeded();
      if (this.companyId !== companyId) {
        try {
          if (this.companyId) await this.connection.invoke('LeaveCompanyGroup', this.companyId);
          await this.connection.invoke('JoinCompanyGroup', companyId);
          this.companyId = companyId;
          console.log(`[KioskSignalR] Grup değişti: Company_${companyId}`);
        } catch (err) {
          console.warn('[KioskSignalR] Grup değişimi başarısız:', err);
        }
      } else {
        try {
          await this.connection.invoke('JoinCompanyGroup', companyId);
        } catch { /* ignore */ }
      }
      return;
    }

    this.companyId = companyId;

    if (this.connection) {
      try { await this.connection.stop(); } catch { /* ignore */ }
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${NETAPI_URL}/hubs/kiosk`, {
        withCredentials: false,
        skipNegotiation: false,
        transport:
          signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => (ctx.previousRetryCount < 3 ? 3000 : 10000),
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onclose(() => {
      console.log('[KioskSignalR] Bağlantı kapandı');
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      this.attemptReconnect();
    });

    this.connection.onreconnecting(() => {
      console.log('[KioskSignalR] Yeniden bağlanıyor...');
      this.notifyStatusChange(signalR.HubConnectionState.Reconnecting);
    });

    this.connection.onreconnected(async () => {
      console.log('[KioskSignalR] Yeniden bağlandı');
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      this.reconnectAttempts = 0;
      await this.joinGroup();
    });

    this.registerListeners();

    try {
      await this.connection.start();
      console.log('[KioskSignalR] Bağlantı kuruldu');
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      await this.joinGroup();
    } catch (err) {
      console.error('[KioskSignalR] Bağlantı başarısız:', err);
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      this.attemptReconnect();
    }
  }

  async disconnect(): Promise<void> {
    if (!this.connection) return;
    try {
      if (this.companyId) {
        try { await this.connection.invoke('LeaveCompanyGroup', this.companyId); } catch { /* ignore */ }
      }
      await this.connection.stop();
    } catch (err) {
      console.warn('[KioskSignalR] Disconnect hatası:', err);
    }
    this.connection = null;
    this.companyId = null;
  }

  private async joinGroup(): Promise<void> {
    if (!this.connection || !this.companyId) return;
    try {
      await this.connection.invoke('JoinCompanyGroup', this.companyId);
      console.log(`[KioskSignalR] Grup'a katıldı: Company_${this.companyId}`);
    } catch (err) {
      console.warn('[KioskSignalR] JoinCompanyGroup hatası:', err);
    }
  }

  private registerListeners(): void {
    if (!this.connection) return;

    // Defansif tip cozumu — Plan 2 1.3'te belirtildigi sekilde:
    // SignalR JsonProtocol policy degisirse (PascalCase → camelCase) kirilmasin.
    this.connection.on('OnKioskProductVisibilityChanged', (raw: any) => {
      const payload: KioskProductVisibilityChangedPayload = {
        KioskId: Number(raw?.KioskId ?? raw?.kioskId ?? 0),
        FirebaseProductId: String(raw?.FirebaseProductId ?? raw?.firebaseProductId ?? ''),
        IsHidden: (raw?.IsHidden ?? raw?.isHidden) === true,
      };
      console.log('🖥️ [KioskSignalR] OnKioskProductVisibilityChanged:', payload, '(raw):', raw);

      if (!payload.KioskId || !payload.FirebaseProductId) {
        console.warn('[KioskSignalR] Gecersiz payload, atlandi:', raw);
        return;
      }

      this.changedCallbacks.forEach((cb) => {
        try { cb(payload); } catch (err) { console.warn('[KioskSignalR] callback hatası:', err); }
      });
    });

    this.connection.on('GroupJoined', (info: any) => {
      console.log('[KioskSignalR] GroupJoined:', info);
    });

    this.connection.on('Pong', (info: any) => {
      console.log('[KioskSignalR] Pong:', info);
    });
  }

  /** HMR/hot-reload sonrası listener'lar kaybolabilir — duplicate önlemek için
   *  off-then-on pattern kullanıyoruz. */
  private reRegisterListenersIfNeeded(): void {
    if (!this.connection) return;
    const eventNames = ['OnKioskProductVisibilityChanged', 'GroupJoined', 'Pong'];
    eventNames.forEach((name) => this.connection!.off(name));
    this.registerListeners();
  }

  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts || !this.companyId) return;
    this.reconnectAttempts++;
    const delay = Math.min(2000 * this.reconnectAttempts, 10000);
    setTimeout(() => {
      console.log(`[KioskSignalR] Reconnect denemesi ${this.reconnectAttempts}`);
      if (this.companyId) this.connect(this.companyId).catch(() => { /* ignore */ });
    }, delay);
  }

  private notifyStatusChange(state: signalR.HubConnectionState): void {
    this.statusChangeCallbacks.forEach((cb) => {
      try { cb(state); } catch { /* ignore */ }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Public callback API
  // ─────────────────────────────────────────────────────────────

  /** Kiosk urun gorunurluk degisim event'ine abone ol. */
  onKioskProductVisibilityChanged(
    id: string,
    callback: (payload: KioskProductVisibilityChangedPayload) => void
  ): void {
    this.changedCallbacks.set(id, callback);
  }

  removeCallback(id: string): void {
    this.changedCallbacks.delete(id);
  }

  onStatusChange(callback: (state: signalR.HubConnectionState) => void): void {
    this.statusChangeCallbacks.push(callback);
  }
}

const kioskSignalRService = new KioskSignalRService();
export default kioskSignalRService;
